import { useEffect, useState } from "react";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { DeliveryStopPod } from "api/response";
import { useGetDeliveryStopsPod } from "api/queries/shipments/useGetDeliveryStopsPod";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import { ReactComponent as ChevronDown } from "assets/images/iconsV2/chevron-down.svg";
import testIds from "constants/testIds";
import { classNames } from "helpers/commonHelpers";
import DeliveryStopPodRow from "./DeliveryStopPodRow";
import MasterPodRow from "./MasterPodRow";
import { mapStopsToMasterPodStatus } from "./podStatus";
import "./ProofOfDeliveryMenu.scss";

export type ProofOfDeliveryMenuProps = Readonly<{
  shipmentId?: string;
  enabled?: boolean;
  onMasterSelect?: () => void;
  onStopSelect?: (stop: DeliveryStopPod) => void;
}>;

export default function ProofOfDeliveryMenu({
  shipmentId: shipmentIdProp,
  enabled = true,
  onMasterSelect,
  onStopSelect,
}: ProofOfDeliveryMenuProps) {
  const intl = useIntl();
  const { id: routeShipmentId } = useParams();
  const shipmentId = shipmentIdProp || routeShipmentId || "";
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading } = useGetDeliveryStopsPod(shipmentId, {
    enabled: enabled && !!shipmentId,
    refetchOnMount: false,
  });

  const { data: trackingReferencesData } = useGetTrackingReferences(shipmentId, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });
  const canSubmitWithoutTracking = trackingReferencesData?.tracking_settings?.can_submit_pod_without_tracking;
  const isBlocked = !canSubmitWithoutTracking;

  const stops = data?.stops ?? [];
  const masterPodStatus = mapStopsToMasterPodStatus(stops);

  // Reset any open state when the trigger becomes blocked so a stale open menu
  // cannot linger and mis-position after tracking requirements change.
  useEffect(() => {
    if (isBlocked && isOpen) {
      setIsOpen(false);
    }
  }, [isBlocked, isOpen]);

  const popover = (
    <Popover id="proof-of-delivery-menu-popover" className="proof-of-delivery-menu-popover">
      <Popover.Content style={{ padding: 0 }} data-testid={testIds.SHIPMENT_DASHBOARD_PROOF_OF_DELIVERY_MENU_LIST}>
        <div className="proof-of-delivery-menu__list" role="menu" aria-busy={isLoading}>
          <MasterPodRow
            status={masterPodStatus}
            testId={testIds.SHIPMENT_DASHBOARD_PROOF_OF_DELIVERY_MENU_MASTER_ROW}
            onSelect={() => {
              setIsOpen(false);
              onMasterSelect?.();
            }}
          />
          {stops.length > 0 && <div className="proof-of-delivery-menu__divider" role="separator" />}
          {stops.map((stop) => (
            <DeliveryStopPodRow
              key={stop.stop_id}
              stop={stop}
              testId={`${testIds.SHIPMENT_DASHBOARD_PROOF_OF_DELIVERY_MENU_ROW_PREFIX}${stop.stop_id}`}
              onSelect={(selectedStop) => {
                setIsOpen(false);
                onStopSelect?.(selectedStop);
              }}
            />
          ))}
        </div>
      </Popover.Content>
    </Popover>
  );

  const tooltip = (
    <Tooltip id="proof-of-delivery-menu-tooltip">
      {intl.formatMessage({
        id: "tracking.components.podDialog.cannotSubmit",
        defaultMessage: "Cannot submit POP or POD without tracking number",
      })}
    </Tooltip>
  );

  // A single OverlayTrigger is kept for both states so the trigger button never
  // remounts when tracking becomes available. Swapping between two separate
  // triggers previously left the popover anchored to a stale target, which made
  // the menu render at the top-right of the page until a full page refresh.
  return (
    <OverlayTrigger
      trigger={isBlocked ? ["hover", "focus"] : "click"}
      placement={isBlocked ? "bottom" : "bottom-end"}
      delay={isBlocked ? { show: 250, hide: 0 } : undefined}
      overlay={isBlocked ? tooltip : popover}
      rootClose={!isBlocked}
      show={isOpen}
      onToggle={setIsOpen}
    >
      <span className="proof-of-delivery-menu__trigger-wrapper">
        <button
          type="button"
          className={classNames("cta-button", "outline", "proof-of-delivery-menu__trigger")}
          aria-haspopup="menu"
          aria-expanded={isBlocked ? undefined : isOpen}
          disabled={isBlocked}
          data-testid={testIds.SHIPMENT_DASHBOARD_PROOF_OF_DELIVERY_MENU_BUTTON}
        >
          <FormattedMessage id="components.molecules.top-nav.proofOfDelivery" defaultMessage="Proof of delivery" />
          <ChevronDown className="proof-of-delivery-menu__chevron" width={16} height={16} aria-hidden />
        </button>
      </span>
    </OverlayTrigger>
  );
}
